import React, { FC, useEffect, useState } from 'react'

import { Container, Row, Col, Form } from 'react-bootstrap'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import Button from '@components/common/Button/Button.component'
import { ScreenLoader } from '@components/ui'
import { FIRST_LOAD_OVERLAY_STYLE } from '@utils/waitAtLeast.util'

import backgroundImage from '@assets/images/login/bg-login.png'
import enamelaiLogo from '@assets/images/login/login-logo.svg'

import { useAppSelector, useAppDispatch } from '@store/hooks'
import { RootState } from '@store/index'
import { login } from '@store/actions/auth.action'

import './LoginStyle.css'
import { isStaff } from '@utils/accountSession.util'
import {
  completePwReset,
  fetchResetContext,
  requestPwReset,
} from '@services/api/auth.api'
import { passwordMatch, loginReset } from '@constants/loginReset.const'
import {
  PwRule,
  policyFail,
  pwRuleLabel,
  pwRuleOrder,
  rulePass,
} from '@utils/passwordPolicy.util'

const COPYRIGHT_YEAR = new Date().getFullYear()

const postLoginPath = (fromPath: string): string => {
  if (fromPath === '/' || fromPath === '/login') {
    return isStaff() ? '/' : '/dashboard'
  }
  if (fromPath === '/accounts') {
    return isStaff() ? '/accounts' : '/dashboard'
  }
  if (fromPath === '/settings' || fromPath.startsWith('/settings/')) {
    return isStaff() ? '/accounts' : '/dashboard'
  }
  return fromPath
}

const Login: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const { loading, error, isAuthenticated } = useAppSelector(
    (state: RootState) => state.auth,
  )
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [resetOpen, setResetOpen] = useState(false)
  const [resetUser, setResetUser] = useState('')
  const [resetEmail, setResetEmail] = useState('')
  const [resetMsg, setResetMsg] = useState<string | null>(null)

  const linkToken =
    searchParams.get(loginReset.queryFlag) === loginReset.flagOn
      ? (searchParams.get(loginReset.queryToken) ?? '').trim()
      : ''
  const isLinkReset = linkToken.length > 0

  const [ctxLoading, setCtxLoading] = useState(false)
  const [ctxBad, setCtxBad] = useState(false)
  const [policyCtx, setPolicyCtx] = useState<{
    username: string
    email: string
  } | null>(null)
  const [newPass, setNewPass] = useState('')
  const [confirmPass, setConfirmPass] = useState('')
  const [showNewPass, setShowNewPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [formErr, setFormErr] = useState<string | null>(null)
  const [submitBusy, setSubmitBusy] = useState(false)

  const from = (location.state as { from?: string })?.from || '/'
  const doneHint = (location.state as { passwordResetDone?: string } | null)
    ?.passwordResetDone

  useEffect(() => {
    if (!isLinkReset) return
    let cancelled = false
    setCtxLoading(true)
    setCtxBad(false)
    setPolicyCtx(null)
    void fetchResetContext(linkToken).then((res) => {
      if (cancelled) return
      setCtxLoading(false)
      if (res.statusCode !== 200 || !res.data?.valid || !res.data.username) {
        setCtxBad(true)
        return
      }
      setPolicyCtx({
        username: res.data.username ?? '',
        email: res.data.email ?? '',
      })
    })
    return () => {
      cancelled = true
    }
  }, [isLinkReset, linkToken])

  const handleLogin = (): void => {
    if (!username || !password)
      return alert('Please fill in your username and password.')

    dispatch(
      login({
        username,
        password,
        scope:
          'read:admin write:admin read:provider write:provider read:patient write:patient',
      }),
    )
      .unwrap()
      .then(() => {
        navigate(postLoginPath(from), { replace: true })
      })
      .catch(() => {
        /* error surfaced via Redux */
      })
  }

  const handleReset = async (): Promise<void> => {
    const u = resetUser.trim()
    const e = resetEmail.trim()
    if (!u || !e) {
      setResetMsg('Please enter your username and email.')
      return
    }
    setResetMsg(null)
    await requestPwReset({ username: u, email: e })
    setResetMsg('We sent a reset link to your email.')
  }

  const handleComplete = async (): Promise<void> => {
    if (!policyCtx) return
    setFormErr(null)
    if (newPass !== confirmPass) {
      setFormErr('New password must match confirmation.')
      return
    }
    const fail = policyFail(newPass, policyCtx)
    if (fail) {
      setFormErr(fail)
      return
    }
    setSubmitBusy(true)
    const res = await completePwReset({ token: linkToken, password: newPass })
    setSubmitBusy(false)
    if (res.statusCode !== 200 || !res.data?.done) {
      setFormErr(res.message || 'Could not update password.')
      return
    }
    navigate('/login', {
      replace: true,
      state: {
        passwordResetDone:
          'Your password was updated. Sign in with your new password.',
      },
    })
  }

  useEffect(() => {
    if (isAuthenticated) {
      navigate(postLoginPath(from), { replace: true })
    }
  }, [isAuthenticated, navigate, from])

  const showMainError = !isLinkReset && !resetOpen

  return (
    <Container
      fluid
      className="loading-container vh-100 d-flex align-items-center justify-content-center"
      style={{ position: 'relative' }}
    >
      <ScreenLoader
        open={loading}
        style={FIRST_LOAD_OVERLAY_STYLE}
        aria-label="Loading"
      />

      <Row className="w-100">
        <Col
          md={7}
          className="d-flex flex-column justify-content-between py-4 px-4"
          style={{ minHeight: '100vh' }}
        >
          <header className="text-start">
            <img src={enamelaiLogo} width="171" height="40" alt="ENAMELAI" />
          </header>
          <main
            className="d-flex flex-column align-items-center justify-content-center"
            style={{ flex: 1 }}
          >
            <div style={{ width: '100%', maxWidth: '360px' }}>
              {isLinkReset ? (
                <>
                  <h3
                    className="mb-1 text-start"
                    style={{ color: '#1B1D22', fontSize: '32px' }}
                  >
                    Set a new password
                  </h3>
                  <h4
                    className="mb-4 text-start"
                    style={{ color: '#4E525A', fontSize: '16px' }}
                  >
                    {ctxLoading
                      ? 'Checking your reset link…'
                      : ctxBad
                        ? 'This link is invalid or has expired.'
                        : 'Choose a strong password you have not used before.'}
                  </h4>
                  {ctxLoading && (
                    <div className="small text-muted mb-3">Please wait…</div>
                  )}
                  {!ctxLoading && ctxBad && (
                    <button
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => navigate('/login', { replace: true })}
                    >
                      Back to sign in
                    </button>
                  )}
                  {!ctxLoading && !ctxBad && policyCtx && (
                    <>
                      <Form.Group controlId="linkResetNew" className="mb-3">
                        <Form.Label>New password</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showNewPass ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="New password"
                            value={newPass}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setNewPass(e.target.value)}
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => setShowNewPass((v) => !v)}
                            aria-label={
                              showNewPass
                                ? 'Hide new password'
                                : 'Show new password'
                            }
                            title={
                              showNewPass ? 'Hide password' : 'Show password'
                            }
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6c757d',
                              textDecoration: 'none',
                            }}
                          >
                            <i
                              className={`ti ${showNewPass ? 'ti-eye-off' : 'ti-eye'}`}
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </Form.Group>
                      <Form.Group controlId="linkResetConfirm" className="mb-3">
                        <Form.Label>Confirm new password</Form.Label>
                        <div className="position-relative">
                          <Form.Control
                            type={showConfirmPass ? 'text' : 'password'}
                            autoComplete="new-password"
                            placeholder="Confirm new password"
                            value={confirmPass}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setConfirmPass(e.target.value)}
                            style={{ paddingRight: '2.5rem' }}
                          />
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => setShowConfirmPass((v) => !v)}
                            aria-label={
                              showConfirmPass
                                ? 'Hide confirm password'
                                : 'Show confirm password'
                            }
                            title={
                              showConfirmPass
                                ? 'Hide password'
                                : 'Show password'
                            }
                            style={{
                              position: 'absolute',
                              right: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6c757d',
                              textDecoration: 'none',
                            }}
                          >
                            <i
                              className={`ti ${
                                showConfirmPass ? 'ti-eye-off' : 'ti-eye'
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        </div>
                      </Form.Group>
                      {formErr && (
                        <div className="small text-danger mb-3" role="alert">
                          {formErr}
                        </div>
                      )}
                      <div className="login-pw-rules" role="list">
                        {pwRuleOrder.map((r: PwRule) => {
                          const ok = rulePass(newPass, policyCtx, r)
                          return (
                            <div
                              key={r}
                              className={`login-pw-rule ${ok ? 'is-pass' : ''}`}
                              role="listitem"
                            >
                              <i
                                className={`ti ${ok ? 'ti-check' : 'ti-circle'}`}
                                aria-hidden="true"
                              />
                              <span>{pwRuleLabel[r]}</span>
                            </div>
                          )
                        })}
                        {confirmPass.length > 0 && (
                          <div
                            className={`login-pw-rule ${
                              newPass === confirmPass ? 'is-pass' : ''
                            }`}
                            role="listitem"
                          >
                            <i
                              className={`ti ${
                                newPass === confirmPass
                                  ? 'ti-check'
                                  : 'ti-circle'
                              }`}
                              aria-hidden="true"
                            />
                            <span>{passwordMatch.matchOk}</span>
                          </div>
                        )}
                      </div>
                      <Button
                        size={'lg'}
                        className={`button-primary button-lg`}
                        disabled={submitBusy}
                        onClick={() => void handleComplete()}
                      >
                        {submitBusy ? 'Saving…' : 'Save password'}
                      </Button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <h3
                    className="mb-1 text-start"
                    style={{ color: '#1B1D22', fontSize: '32px' }}
                  >
                    {showMainError && typeof error === 'string'
                      ? error.split('. ')[0]
                      : 'Sign in to your account'}
                  </h3>
                  <h4
                    className="mb-5 text-start"
                    style={{ color: '#4E525A', fontSize: '16px' }}
                  >
                    {showMainError && typeof error === 'string'
                      ? error.split('. ')[1]
                      : 'Welcome back! Please enter your details.'}
                  </h4>
                  {resetMsg && (
                    <div
                      className={`small mb-3 ${
                        resetMsg === 'We sent a reset link to your email.'
                          ? 'text-success'
                          : 'text-muted'
                      }`}
                      role="status"
                    >
                      {resetMsg}
                    </div>
                  )}
                  {resetOpen ? (
                    <>
                      <Form.Group controlId="resetUsername">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          placeholder="Username"
                          value={resetUser}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setResetUser(e.target.value)
                          }
                        />
                      </Form.Group>
                      <Form.Group controlId="resetEmail" className="my-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Email"
                          value={resetEmail}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setResetEmail(e.target.value)
                          }
                        />
                      </Form.Group>
                      <Button
                        size={'lg'}
                        className={`button-primary button-lg`}
                        onClick={() => void handleReset()}
                      >
                        Send reset link
                      </Button>
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            setResetOpen(false)
                            setResetMsg(null)
                          }}
                        >
                          Back to sign in
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {doneHint && (
                        <div className="small text-success mb-3" role="status">
                          {doneHint}
                        </div>
                      )}
                      <Form.Group controlId="formBasicEmail">
                        <Form.Label>Username</Form.Label>
                        <Form.Control
                          type="username"
                          placeholder="Username"
                          required
                          value={username}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setUsername(e.target.value)
                          }
                        />
                      </Form.Group>
                      <Form.Group
                        controlId="formBasicPassword"
                        className="my-3"
                      >
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Password"
                          required
                          value={password}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setPassword(e.target.value)
                          }
                        />
                      </Form.Group>
                      <Row className="mb-3 align-items-center"></Row>
                      <Button
                        size={'lg'}
                        className={`button-primary button-lg`}
                        onClick={handleLogin}
                      >
                        Sign in
                      </Button>
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            setResetOpen(true)
                            setResetMsg(null)
                            setResetUser(username)
                          }}
                        >
                          Forgot password?
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </main>
          <footer className="w-100">
            <Row className="justify-content-between">
              <Col
                md="auto"
                className="text-start"
                style={{
                  color: '#4E525A',
                  fontSize: '14px',
                  fontWeight: '400',
                }}
              >
                © ENAMELAI {COPYRIGHT_YEAR}
              </Col>
              <Col
                md="auto"
                className="text-end"
                style={{
                  color: '#4E525A',
                  fontSize: '14px',
                  fontWeight: '400',
                }}
              >
                <div>
                  <i className="fa-regular fa-envelope"></i>{' '}
                  support@enamelai.com
                </div>
              </Col>
            </Row>
          </footer>
        </Col>
        <Col
          md={5}
          style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            borderTopLeftRadius: '5rem',
            borderBottomLeftRadius: '5rem',
          }}
        >
          <div className="text-white p-5">
            {/* Optional content for the right column */}
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default Login
